import type { ETLSourceResponse } from '@/model/etlSource/dto.generated';
import type { ETLTaskChainResponse } from '@/model/etlTasks/dto.generated';
import { useTrans } from '@/shared/hooks/useTranslation';
import { useFailureSummary } from '@/viewmodel/features/observability/hooks/useFailureSummary';
import {
  TOAST_DURATION_MS,
  causeFromSummary,
  toSourceMap,
} from '@/viewmodel/features/observability/mappers/runNotificationData';
import type { PendingFailure } from '@/viewmodel/features/observability/types';
import type { SourceStatusView } from '@/viewmodel/features/sourceState';
import { notify } from '@/viewmodel/shared/notify';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export function useChainRunNotifications(
  allChains: ETLTaskChainResponse[] | undefined,
  sources: ETLSourceResponse[] | undefined,
  statusById: ReadonlyMap<string, SourceStatusView>,
  workspaceId: string | undefined,
): void {
  const { t } = useTrans();
  const navigate = useNavigate();
  const location = useLocation();
  const seenTerminalChainsRef = useRef<Set<string>>(new Set());
  const notifiedChainsRef = useRef<Set<string>>(new Set());
  const chainsInitializedRef = useRef(false);
  const [pendingFailure, setPendingFailure] = useState<PendingFailure | null>(null);

  const canFetchSummary = !!pendingFailure?.correlationId && !!workspaceId;

  const {
    data: failureSummary,
    isSuccess: summaryLoaded,
    isError: summaryFailed,
  } = useFailureSummary(
    canFetchSummary
      ? { workspaceId: workspaceId ?? '', correlationId: pendingFailure?.correlationId ?? '' }
      : { workspaceId: '', correlationId: '' },
    { skip: !canFetchSummary },
  );

  useEffect(() => {
    if (allChains === undefined) return;

    const chains = allChains.filter((c) => c.kind === 'setup' || c.kind === 'refresh');
    const terminalChains = chains.filter((c) => c.status === 'SUCCESS' || c.status === 'FAILED');

    if (!chainsInitializedRef.current) {
      for (const chain of terminalChains) {
        if (chain.id) seenTerminalChainsRef.current.add(chain.id);
      }
      chainsInitializedRef.current = true;
      return;
    }

    const sourceMap = toSourceMap(sources ?? []);

    for (const chain of terminalChains) {
      if (!chain.id) continue;
      if (seenTerminalChainsRef.current.has(chain.id)) continue;
      if (notifiedChainsRef.current.has(chain.id)) continue;

      const source = chain.etl_source_id ? sourceMap.get(chain.etl_source_id) : undefined;
      const slug = source?.slug ?? source?.name ?? chain.etl_source_id ?? '';

      if (chain.status === 'SUCCESS') {
        notifiedChainsRef.current.add(chain.id);
        notify(t('notifications.silver.success', { slug }), 'success', {
          duration: TOAST_DURATION_MS,
        });
        continue;
      }

      if (chain.kind === 'setup') {
        const status = chain.etl_source_id ? statusById.get(chain.etl_source_id) : undefined;
        const isReady = status?.isConnected === true;
        const isFailed = status?.isFailed === true;
        if (!isReady && !isFailed) continue;

        if (isReady) {
          notifiedChainsRef.current.add(chain.id);
          notify(t('notifications.silver.success', { slug }), 'success', {
            duration: TOAST_DURATION_MS,
          });
          continue;
        }
      }

      notifiedChainsRef.current.add(chain.id);

      if (!chain.correlation_id || !workspaceId) {
        notify(t('notifications.failure.toastGeneric', { name: slug }), 'danger', {
          duration: TOAST_DURATION_MS,
        });
        continue;
      }

      setPendingFailure({ chainId: chain.id, correlationId: chain.correlation_id, name: slug });
    }
  }, [allChains, sources, statusById, t, workspaceId]);

  useEffect(() => {
    if (!pendingFailure) return;
    if (!summaryLoaded && !summaryFailed) return;

    const cause = causeFromSummary(failureSummary);
    const message = cause
      ? t('notifications.failure.toast', { cause })
      : t('notifications.failure.toastGeneric', { name: pendingFailure.name });

    const correlationId = pendingFailure.correlationId;
    const hasDeepLink = !!correlationId && !!workspaceId;
    const action = hasDeepLink
      ? {
          label: t('notifications.failure.viewDetails'),
          onPress: () => {
            const next = new URLSearchParams(location.search);
            next.set('activity', correlationId);
            navigate({ search: next.toString() }, { replace: true });
          },
        }
      : undefined;

    notify(message, 'danger', { duration: TOAST_DURATION_MS, action });
    setPendingFailure(null);
  }, [
    pendingFailure,
    summaryLoaded,
    summaryFailed,
    failureSummary,
    t,
    navigate,
    location.search,
    workspaceId,
  ]);
}
